/**
 * StackCard — single worksheet card visual for the home stack.
 *
 * Used by the home swipe stack to render the top + peek positions.
 *
 * Visual contract:
 *  - Familiar 5:7 playing/baseball-card proportion
 *  - White bg, soft border, rounded corners, and physical-card shadow
 *  - Full worksheet preview with contain (never crop meaningful content)
 *  - Hero badge for isHero cards (mint bg, "Rekomendasi untuk [Nama Anak]")
 *  - Title (22pt semibold, max 2 lines)
 *  - Tag pills (13pt): tema, activity
 *  - AI explanation placeholder (Phase 3.4: real template)
 *  - shadow-md for top card, shadow-sm for peek, none for #3
 */

import { Image, StyleSheet, View } from 'react-native';

import { ThemedText } from '@/components/themed-text';
import { AppIcon } from '@/components/app-icon';
import { Brand, Radii, Spacing } from '@/constants/theme';
import { activityIcon, MISC_ICON } from '@/lib/icons';
import type { WorksheetCard } from '@/types/daily';

export type StackCardProps = {
  card: WorksheetCard;
  /** "top" = position 1 (full); "peek" = position 2/3 (scaled back). */
  variant: 'top' | 'peek';
  /** Active child name for hero badge copy. */
  childName: string;
  /** Show the immediate next card's title in the visible spill below the top card. */
  showPeekTitle?: boolean;
};

const TAG_BG = '#F5F0FA';
const TAG_TEXT = '#563F7A';
const HERO_BG = '#7FD1B9';
const HERO_TEXT = '#1A4A40';

export function StackCard({ card, variant, childName, showPeekTitle = false }: StackCardProps) {
  const isTopCard = variant === 'top';
  const previewUrl = card.imageUrl || card.thumbnailUrl;

  return (
    <View
      style={[
        styles.card,
        variant === 'top' ? styles.cardTop : styles.cardPeek,
      ]}
    >
      {isTopCard && card.isHero ? (
        <View style={styles.heroBadge}>
          <AppIcon source={MISC_ICON.sparkle} size={14} />
          <ThemedText style={styles.heroBadgeText} numberOfLines={1}>
            Rekomendasi untuk {childName}
          </ThemedText>
        </View>
      ) : null}

      {/* Preview — full worksheet visible (resizeMode contain). The badge is
          outside this area so it never covers meaningful worksheet content.
          Render it for peek cards too so the next worksheet is already loaded
          before it becomes the active card. */}
      <View style={styles.thumbWrap}>
        {previewUrl ? (
          <Image
            source={{ uri: previewUrl }}
            style={styles.thumb}
            resizeMode="contain"
            // expo-image would be ideal, but stock Image is fine for 3.1.
            // Phase 3.4 swaps to expo-image for caching.
          />
        ) : (
          <View style={[styles.thumb, styles.thumbPlaceholder]}>
            <AppIcon
              source={card.isHero ? MISC_ICON.sparkle : activityIcon(card.activityLabel)}
              size={72}
            />
          </View>
        )}
      </View>

      {/* Body */}
      {isTopCard ? (
        <View style={styles.body}>
          {card.reviewReasonLabel ? (
            <View style={styles.reviewBadge}>
              <ThemedText style={styles.reviewBadgeText} numberOfLines={1}>
                Pernah dilewati · {card.reviewReasonLabel}
              </ThemedText>
            </View>
          ) : null}
          <ThemedText style={styles.title} numberOfLines={2}>
            {card.name}
          </ThemedText>
          <View style={styles.tags}>
            {/*
              Phase 3.5: prefer 14 PAUD tema name from server. Fall back
              to legacy 3 generic themeLabel when enrichment hasn't mapped
              this worksheet yet (e.g. newly added worksheets before the
              next cron sync).
            */}
            {card.temaNames && card.temaNames.length > 0 ? (
              card.temaNames.slice(0, 2).map((t) => (
                <View key={t} style={styles.tag}>
                  <ThemedText style={styles.tagText} numberOfLines={1}>
                    {t}
                  </ThemedText>
                </View>
              ))
            ) : card.themeLabel ? (
              <View style={styles.tag}>
                <ThemedText style={styles.tagText} numberOfLines={1}>
                  {card.themeLabel}
                </ThemedText>
              </View>
            ) : null}
            {card.activityLabel ? (
              <View style={styles.tag}>
                <ThemedText style={styles.tagText} numberOfLines={1}>
                  {card.activityLabel}
                </ThemedText>
              </View>
            ) : null}
          </View>
          {/* AI explanation placeholder (real template in 3.4) */}
          <ThemedText style={styles.explanation} numberOfLines={2}>
            Worksheet pilihan untuk {childName}.
          </ThemedText>
        </View>
      ) : null}

      {showPeekTitle ? (
        <View style={styles.peekTitleBand} pointerEvents="none">
          <ThemedText style={styles.peekTitle} numberOfLines={1}>
            {card.name}
          </ThemedText>
        </View>
      ) : null}
    </View>
  );
}

const styles = StyleSheet.create({
  card: {
    width: '100%',
    maxWidth: 300,
    alignSelf: 'center',
    aspectRatio: 5 / 7,
    backgroundColor: '#ffffff',
    borderRadius: 18,
    borderWidth: 1,
    borderColor: '#E5E5E5',
    overflow: 'hidden',
  },
  cardTop: {
    shadowColor: '#33234d',
    shadowOffset: { width: 0, height: 6 },
    shadowOpacity: 0.16,
    shadowRadius: 16,
    elevation: 6,
  },
  cardPeek: {
    shadowColor: '#33234d',
    shadowOffset: { width: 0, height: 3 },
    shadowOpacity: 0.08,
    shadowRadius: 8,
    elevation: 3,
  },
  thumbWrap: {
    flex: 1,
    minHeight: 0,
    width: '100%',
    backgroundColor: '#f5f3ee',
    position: 'relative',
  },
  thumb: {
    width: '100%',
    height: '100%',
  },
  thumbPlaceholder: {
    alignItems: 'center',
    justifyContent: 'center',
  },
  heroBadge: {
    alignSelf: 'stretch',
    marginTop: Spacing.two,
    marginHorizontal: Spacing.two,
    marginBottom: Spacing.two,
    backgroundColor: HERO_BG,
    paddingVertical: 6,
    paddingHorizontal: Spacing.two,
    borderRadius: Radii.pill,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
  },
  heroBadgeText: {
    color: HERO_TEXT,
    fontSize: 12,
    fontWeight: '700',
    textAlign: 'center',
  },
  body: {
    padding: Spacing.three,
    gap: Spacing.two,
  },
  title: {
    fontSize: 18,
    fontWeight: '600',
    color: Brand.primary,
  },
  tags: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: Spacing.one,
  },
  tag: {
    backgroundColor: TAG_BG,
    paddingVertical: 4,
    paddingHorizontal: Spacing.two,
    borderRadius: Radii.pill,
  },
  tagText: {
    color: TAG_TEXT,
    fontSize: 12,
    fontWeight: '600',
  },
  explanation: {
    fontSize: 13,
    color: '#6b6573',
    lineHeight: 18,
    fontStyle: 'italic',
  },
  reviewBadge: {
    alignSelf: 'flex-start',
    backgroundColor: '#FFF0E4',
    paddingVertical: 4,
    paddingHorizontal: Spacing.two,
    borderRadius: Radii.pill,
  },
  reviewBadgeText: {
    color: Brand.accent,
    fontSize: 11,
    fontWeight: '700',
  },
  peekTitleBand: {
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: 0,
    minHeight: 26,
    justifyContent: 'center',
    paddingHorizontal: Spacing.three,
    paddingVertical: Spacing.one,
    backgroundColor: '#ffffff',
    borderTopWidth: 1,
    borderTopColor: '#E5E5E5',
  },
  peekTitle: {
    color: Brand.primary,
    fontSize: 14,
    fontWeight: '700',
  },
});
